import { useRef, useState, type ReactNode } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  COUNTRY_CODES,
  DOCUMENT_CONTENT_TYPES,
  EMERGENCY_RELATIONSHIPS,
  GENDERS,
  LICENCE_FEDERATIONS,
  checklistKey,
  documentUploadInput,
  type ChecklistItem,
  type ChecklistKey,
  type ProfileResponse,
} from '@gg/shared';
import {
  Button,
  Card,
  ChecklistRow,
  Divider,
  Notice,
  SelectField,
  TextField,
  type ChecklistTone,
} from '@/components/ui';
import { ApiRequestError } from '@/lib/api';
import { fileProblem, openDocument } from '@/lib/documentUpload';
import { formatDate } from '@/lib/dates';
import {
  apiFieldError,
  checkField,
  formValuesOf,
  type ProfileFormPath,
  type ProfileFormValues,
} from '@/lib/profileForm';
import { useSaveProfileField, useUploadCertificate } from '@/lib/queries';

type SaveState = 'saving' | 'saved' | 'error';

const TONES: Record<ChecklistItem['state'], ChecklistTone> = {
  done: 'done',
  todo: 'todo',
  submitted: 'pending',
  rejected: 'error',
  expired: 'error',
  optional: 'optional',
};

/**
 * "À compléter avant la course" (screen 06, P-11 / P-12): one row per part of the pre-race
 * profile with its state; a row opens its fields, each saved when left (no submit button) with
 * its own saved / error state. The completion and the checklist come from the API.
 */
export function ProfileChecklist({ data }: { data: ProfileResponse }) {
  const { t, i18n } = useTranslation();
  // A link to `/me#profile-documents` (the "document refused" email) opens that row.
  const [open, setOpen] = useState<ChecklistKey | null>(() => {
    const key = window.location.hash.replace('#profile-', '');
    return checklistKey.safeParse(key).success ? (key as ChecklistKey) : null;
  });
  const [status, setStatus] = useState<Partial<Record<ProfileFormPath, SaveState>>>({});
  const save = useSaveProfileField();
  const form = useForm<ProfileFormValues>({ defaultValues: formValuesOf(data.profile) });
  const { register, getValues, setValue, setError, clearErrors, formState } = form;
  // Latest profile for the checks, without waiting for a re-render between two saves.
  const latest = useRef(data);
  latest.current = data;

  const persist = async (path: ProfileFormPath) => {
    const raw = String(getValues(path) ?? '');
    const check = checkField(latest.current.profile, path, raw);
    if (!check.ok) {
      setError(path, { message: check.error });
      setStatus((s) => ({ ...s, [path]: 'error' }));
      return;
    }
    clearErrors(path);
    if (check.unchanged) {
      setStatus((s) => (s[path] === 'error' ? { ...s, [path]: undefined } : s));
      return;
    }
    setStatus((s) => ({ ...s, [path]: 'saving' }));
    try {
      const next = await save.mutateAsync(check.body);
      latest.current = next;
      // Show the stored form (e.g. a phone number in E.164) unless the input changed meanwhile.
      const stored = formValuesOf(next.profile);
      const value = path.startsWith('address.')
        ? stored.address[path.slice('address.'.length) as keyof ProfileFormValues['address']]
        : stored[path as Exclude<ProfileFormPath, `address.${string}`>];
      if (String(getValues(path) ?? '') === raw) setValue(path, value as never);
      setStatus((s) => ({ ...s, [path]: 'saved' }));
    } catch (e) {
      const message = e instanceof ApiRequestError ? apiFieldError(e.details, path) : null;
      setError(path, { message: message ?? 'profile.save.failed' });
      setStatus((s) => ({ ...s, [path]: 'error' }));
    }
  };

  const errorOf = (path: ProfileFormPath) => {
    const [head, sub] = path.split('.') as [keyof ProfileFormValues, string | undefined];
    const err = sub
      ? (formState.errors.address as Record<string, { message?: string }> | undefined)?.[sub]
      : (formState.errors[head] as { message?: string } | undefined);
    return err?.message ? t(err.message) : undefined;
  };
  const hintOf = (path: ProfileFormPath, fallback?: ReactNode) =>
    status[path] === 'saving'
      ? t('profile.save.saving')
      : status[path] === 'saved'
        ? t('profile.save.saved')
        : fallback;
  const toneOf = (path: ProfileFormPath) => (status[path] === 'saved' ? 'success' : 'muted');

  /** Text input saved on blur. */
  const text = (
    path: ProfileFormPath,
    props: Partial<Parameters<typeof TextField>[0]> = {},
    hint?: ReactNode,
  ) => {
    const reg: UseFormRegisterReturn = register(path, { onBlur: () => persist(path) });
    return (
      <TextField
        {...reg}
        {...props}
        label={t(`profile.field.${labelKey(path)}`)}
        error={errorOf(path)}
        hint={hintOf(path, hint)}
        hintTone={toneOf(path)}
      />
    );
  };
  /** Select saved on change. */
  const select = (path: ProfileFormPath, options: { value: string; label: string }[]) => {
    const reg = register(path, { onChange: () => persist(path) });
    return (
      <SelectField
        {...reg}
        label={t(`profile.field.${labelKey(path)}`)}
        error={errorOf(path)}
        hint={hintOf(path)}
        hintTone={toneOf(path)}
      >
        <option value="">{t('profile.select.placeholder')}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </SelectField>
    );
  };

  const countries = COUNTRY_CODES.map((c) => ({ value: c, label: t(`country.${c}`) })).sort(
    (a, b) => a.label.localeCompare(b.label, i18n.language),
  );
  const p = data.profile;

  const summary: Record<ChecklistKey, string | null> = {
    identity: [
      [p.firstName, p.lastName].filter(Boolean).join(' '),
      p.birthDate && formatDate(`${p.birthDate}T12:00:00Z`),
      p.nationality,
    ]
      .filter(Boolean)
      .join(' · '),
    contact: [p.phone, p.address?.city].filter(Boolean).join(' · '),
    emergency: [
      p.emergencyContactName,
      p.emergencyContactRelationship && t(`profile.relationship.${p.emergencyContactRelationship}`),
    ]
      .filter(Boolean)
      .join(' · '),
    documents:
      p.licenceStatus === 'approved' && p.licenceFederation && p.licenceNumber
        ? `${p.licenceFederation} · ${p.licenceNumber}`
        : data.certificateState === 'approved' && data.certificate?.expiresOn
          ? t('profile.docs.validUntil', {
              date: formatDate(`${data.certificate.expiresOn}T12:00:00Z`),
            })
          : null,
    club: p.clubName,
  };

  const panels: Record<ChecklistKey, ReactNode> = {
    identity: (
      <>
        {text('firstName', { autoComplete: 'given-name' })}
        {text('lastName', { autoComplete: 'family-name' })}
        {text('birthDate', { type: 'date', autoComplete: 'bday', max: today() })}
        {select(
          'gender',
          GENDERS.map((g) => ({ value: g, label: t(`profile.gender.${g}`) })),
        )}
        {select('nationality', countries)}
      </>
    ),
    contact: (
      <>
        {text(
          'phone',
          { type: 'tel', autoComplete: 'tel', inputMode: 'tel' },
          t('profile.field.phoneHint'),
        )}
        {text('address.line1', { autoComplete: 'address-line1' })}
        {text('address.line2', { autoComplete: 'address-line2' })}
        <div className="grid grid-cols-[2fr_3fr] gap-3">
          {text('address.postalCode', { autoComplete: 'postal-code' })}
          {text('address.city', { autoComplete: 'address-level2' })}
        </div>
        {select('address.country', countries)}
      </>
    ),
    emergency: (
      <>
        {text('emergencyContactName', { autoComplete: 'off' })}
        {text('emergencyContactPhone', { type: 'tel', inputMode: 'tel', autoComplete: 'off' })}
        {select(
          'emergencyContactRelationship',
          EMERGENCY_RELATIONSHIPS.map((r) => ({ value: r, label: t(`profile.relationship.${r}`) })),
        )}
      </>
    ),
    documents: (
      <>
        <CertificateBlock data={data} />
        <Divider>{t('profile.docs.or')}</Divider>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-bold">{t('profile.docs.licenceTitle')}</span>
          <span className="text-helper text-ink-muted">{t('profile.docs.licenceHint')}</span>
        </div>
        {select(
          'licenceFederation',
          LICENCE_FEDERATIONS.map((f) => ({ value: f, label: t(`profile.federation.${f}`) })),
        )}
        {text('licenceNumber', { autoComplete: 'off', autoCapitalize: 'characters' })}
        {p.licenceStatus === 'pending_review' && (
          <Notice>{t('profile.docs.licencePending')}</Notice>
        )}
        {p.licenceStatus === 'approved' && (
          <p className="m-0 text-helper font-semibold text-success">
            {t('profile.docs.licenceApproved')}
          </p>
        )}
        {p.licenceStatus === 'rejected' && (
          <p className="m-0 text-helper text-danger">
            {t('profile.docs.licenceRejected', { reason: p.licenceRejectionReason ?? '' })}
          </p>
        )}
      </>
    ),
    club: <>{text('clubName', { autoComplete: 'organization' })}</>,
  };

  return (
    <Card aria-labelledby="checklist-title" className="gap-1!">
      <h2 id="checklist-title" className="m-0 mb-1.5 font-display text-section font-bold">
        {t('profile.checklist.title')}
      </h2>
      {data.completion.items.map((item) => {
        const done = item.state === 'done';
        const hint =
          item.state === 'submitted'
            ? t('profile.hint.submitted')
            : item.state === 'rejected'
              ? t('profile.hint.rejected')
              : item.state === 'expired'
                ? t('profile.hint.expired')
                : done && summary[item.key]
                  ? summary[item.key]
                  : t(`profile.hint.${item.key}`);
        return (
          <ChecklistRow
            key={item.key}
            id={`profile-${item.key}`}
            tone={TONES[item.state]}
            title={t(`profile.item.${item.key}`)}
            hint={hint}
            hintTone={
              item.state === 'todo'
                ? 'accent'
                : item.state === 'rejected' || item.state === 'expired'
                  ? 'danger'
                  : 'muted'
            }
            stateLabel={t(`profile.state.${item.state}`)}
            open={open === item.key}
            onToggle={() => setOpen((o) => (o === item.key ? null : item.key))}
          >
            {panels[item.key]}
          </ChecklistRow>
        );
      })}
    </Card>
  );
}

const labelKey = (path: ProfileFormPath) =>
  ({
    'address.line1': 'addressLine1',
    'address.line2': 'addressLine2',
    'address.postalCode': 'postalCode',
    'address.city': 'city',
    'address.country': 'country',
  })[path as string] ?? path;

const today = () => new Date().toISOString().slice(0, 10);

/** Medical certificate (P-12): status, file link, upload or re-upload after a rejection. */
function CertificateBlock({ data }: { data: ProfileResponse }) {
  const { t } = useTranslation();
  const upload = useUploadCertificate();
  const input = useRef<HTMLInputElement>(null);
  const [issuedOn, setIssuedOn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cert = data.certificate;
  const state = data.certificateState;
  // A rejected or an expired certificate is replaced by a new one (P-12).
  const canUpload = state === 'none' || state === 'rejected' || state === 'expired';
  const day = (d: string) => formatDate(`${d}T12:00:00Z`);
  const dateOk = documentUploadInput.shape.issuedOn.safeParse(issuedOn).success;

  const onFile = async (file: File | undefined) => {
    if (input.current) input.current.value = '';
    if (!file) return;
    const problem = fileProblem(file);
    if (problem) return setError(t(problem));
    setError(null);
    try {
      await upload.mutateAsync({ file, issuedOn });
      setIssuedOn('');
    } catch (e) {
      const code = e instanceof ApiRequestError ? e.code : '';
      setError(t(`profile.docs.error.${code}`, { defaultValue: t('profile.docs.error.generic') }));
    }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-sm font-bold">{t('profile.docs.certificate')}</span>
      {state === 'pending_review' && <Notice>{t('profile.docs.pending')}</Notice>}
      {state === 'approved' && cert?.expiresOn && (
        <p className="m-0 text-helper font-semibold text-success">
          {t('profile.docs.validUntil', { date: day(cert.expiresOn) })}
        </p>
      )}
      {state === 'expired' && (
        <p className="m-0 text-helper text-danger">
          {cert?.expiresOn
            ? t('profile.docs.expired', { date: day(cert.expiresOn) })
            : t('profile.hint.expired')}
        </p>
      )}
      {state === 'rejected' && (
        <p className="m-0 text-helper text-danger">
          {t('profile.docs.rejected', { reason: cert?.rejectionReason ?? '' })}
        </p>
      )}
      {cert && (state === 'pending_review' || state === 'approved') && (
        <Button
          variant="ghost"
          size="md"
          onClick={() => openDocument(cert.id).catch(() => setError(t('error.generic')))}
        >
          {t('profile.docs.view')}
        </Button>
      )}
      {canUpload && (
        <>
          <TextField
            type="date"
            label={t('profile.docs.issuedOn')}
            hint={t('profile.docs.issuedOnHint')}
            max={new Date().toISOString().slice(0, 10)}
            value={issuedOn}
            onChange={(e) => setIssuedOn(e.target.value)}
          />
          <input
            ref={input}
            type="file"
            accept={Object.keys(DOCUMENT_CONTENT_TYPES).join(',')}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <Button
            variant="secondary"
            size="md"
            loading={upload.isPending}
            disabled={!dateOk}
            onClick={() => input.current?.click()}
          >
            {upload.isPending
              ? t('profile.docs.uploading')
              : t(state === 'none' ? 'profile.docs.upload' : 'profile.docs.reupload')}
          </Button>
          <span className="text-helper text-ink-muted">{t('profile.hint.documents')}</span>
        </>
      )}
      {error && (
        <p role="alert" className="m-0 text-helper text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
